/**
 * HUD — Panel superior-izquierdo con estado del paciente, notificaciones
 * transitorias y pantalla de carga.
 *
 * Recibe TriageClassifier para resolver color/label de prioridad. Los métodos
 * update* consultan state global y actualizan el DOM in-place (sin re-render).
 * Las notificaciones se auto-remueven a los 4s.
 */

import state from '../core/state';
import { TriageClassifier } from '../medical/TriageClassifier';
import { Patient } from '../medical/Patient';

type NotificationType = 'info' | 'warning' | 'critical';

const TOPOLOGY_LABELS = ['Anatómico', 'Fisiopatológico', 'Temporal', 'Diferencial'] as const;

export class HUD {
  private loadingScreen: HTMLElement | null = null;

  constructor(private triageClassifier: TriageClassifier) {}

  mount(): void {
    const hud = document.createElement('div');
    hud.className = 'cgt-hud';
    hud.innerHTML = `
      <div style="display: flex; align-items: center; margin-bottom: 8px;">
        <span id="triage-dot" class="cgt-triage-indicator" style="background: #10B981;"></span>
        <span id="triage-text" style="font-weight: 600;">AMBULATORIO</span>
      </div>
      <div style="font-size: 12px; color: #888; margin-bottom: 4px;">
        Paciente: <span id="patient-id">--</span>
      </div>
      <div style="font-size: 12px; color: #888;">
        Modo: <span id="topology-mode">Anatómico</span>
      </div>
      <div style="font-size: 12px; color: #888; margin-top: 8px;">
        <div>Tiempo: <span id="sim-time">0</span>x</div>
        <div>Nodos: <span id="node-count">0</span></div>
      </div>
    `;
    document.body.appendChild(hud);
  }

  /**
   * Refresca los campos que dependen del paciente: triage + id + conteo nodos.
   * Llamar al cargar paciente y tras cambios de prioridad.
   */
  updatePatient(patient: Patient | null): void {
    if (!patient) return;

    const triageDot = document.getElementById('triage-dot');
    const triageText = document.getElementById('triage-text');
    const patientId = document.getElementById('patient-id');
    const nodeCount = document.getElementById('node-count');

    if (triageDot && triageText) {
      const color = this.triageClassifier.getTriageColor(patient.data.triagePriority);
      const label = this.triageClassifier.getTriageLabel(patient.data.triagePriority);
      triageDot.style.background = color;
      triageText.textContent = label;
    }
    if (patientId) patientId.textContent = patient.data.id;
    if (nodeCount) nodeCount.textContent = String(patient.nodes.size);
  }

  /** Campos que cambian cada frame (topología, time scale). */
  updateDebug(): void {
    const simTime = document.getElementById('sim-time');
    const topologyMode = document.getElementById('topology-mode');
    if (simTime) simTime.textContent = state.simulation.timeScale.toFixed(0);
    if (topologyMode) topologyMode.textContent = TOPOLOGY_LABELS[state.topologyMode as unknown as number] || 'Anatómico';
  }

  /** Notificación transitoria abajo-centro. Se auto-remueve a los 4s. */
  notify(title: string, message: string, type: NotificationType = 'info'): void {
    const notif = document.createElement('div');
    notif.className = 'cgt-notification';
    notif.style.borderLeftColor =
      type === 'critical' ? '#DC2626' : type === 'warning' ? '#F59E0B' : '#3B82F6';
    notif.innerHTML = `
      <strong>${title}</strong><br>
      <span style="font-size: 12px; opacity: 0.8;">${message}</span>
    `;
    document.body.appendChild(notif);

    setTimeout(() => {
      notif.style.opacity = '0';
      notif.style.transition = 'opacity 0.5s';
      setTimeout(() => notif.remove(), 500);
    }, 4000);
  }

  showLoading(): void {
    this.loadingScreen = document.createElement('div');
    this.loadingScreen.className = 'cgt-loading';
    this.loadingScreen.innerHTML = `
      <div class="cgt-loading-spinner"></div>
      <h2>Clinical Graph Triage</h2>
      <p id="loading-text">Inicializando sistema médico...</p>
      <p style="font-size: 12px; color: #666; margin-top: 20px;">
        ⚠️ Solo para demostración educativa
      </p>
    `;
    document.body.appendChild(this.loadingScreen);
  }

  setLoadingText(text: string): void {
    const el = document.getElementById('loading-text');
    if (el) el.textContent = text;
  }

  hideLoading(): void {
    if (!this.loadingScreen) return;
    this.loadingScreen.style.opacity = '0';
    this.loadingScreen.style.transition = 'opacity 0.5s';
    setTimeout(() => this.loadingScreen?.remove(), 500);
  }
}

export default HUD;
